// src/report/ArchitectureGraph.jsx

import React, { useState, useMemo, useRef, useEffect } from "react";

import ForceGraph2D from "react-force-graph-2d";


export default function ArchitectureGraph({ graph }) {

    const [hoverNode, setHoverNode] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [viewMode, setViewMode] = useState("modules");
    const [searchQuery, setSearchQuery] = useState("");

    const graphRef = useRef(null);
    const forceGraphRef = useRef(null);

    const [graphSize, setGraphSize] = useState({
        width: 900,
        height: 600
    });

    const data = useMemo(() => {

        if (!graph?.data?.graph) {
            return {
                nodes: [],
                links: []
            };
        }

        const raw = graph?.data?.graph;

        if (viewMode === "classes") {
            const classNodes = raw.nodes || [];
            const classEdges = raw.edges || [];

            const maxClassRisk = Math.max(
                1,
                ...classNodes.map(node => node.dependencyScore || 0)
            );

            const nodes = classNodes.map(node => ({
                id: node.nodeId,
                className: node.className,
                packageName: node.packageName,
                module: node.module,
                role: node.role,
                degree: 0,
                dependencies: [],
                usedBy: [],
                risk: node.dependencyScore || 0,
                normalizedRisk: (node.dependencyScore || 0) / maxClassRisk
            }));

            const nodeIds = new Set(nodes.map(node => node.id));

            const links = classEdges
                .filter(edge =>
                    nodeIds.has(edge.fromClass) &&
                    nodeIds.has(edge.toClass)
                )
                .map(edge => ({
                    source: edge.fromClass,
                    target: edge.toClass,
                    type: edge.type,
                    weight: edge.weight,
                    sourceModule: edge.fromModule,
                    targetModule: edge.toModule
                }));

            const degree = {};
            const nodesById = new Map(nodes.map(node => [node.id, node]));

            links.forEach(link => {
                degree[link.source] = (degree[link.source] || 0) + 1;
                degree[link.target] = (degree[link.target] || 0) + 1;

                nodesById.get(link.source)?.dependencies.push(link.target);
                nodesById.get(link.target)?.usedBy.push(link.source);
            });

            nodes.forEach(node => {
                node.degree = degree[node.id] || 0;
            });

            return { nodes, links };
        }

        const riskMap = {};

        (raw.findings || []).forEach(f => {

            if (f.scope === "MODULE") {
                riskMap[f.target] = f.score;
            }

        });

        const maxRisk = Math.max(1, ...Object.values(riskMap));

        const modules = raw.modules || [];

        const nodes = modules.map(module => {

            const moduleFindings = (raw.findings || [])
                .filter(
                    f =>
                        f.scope === "MODULE" &&
                        f.target === module
                );

            const classFindings = (raw.findings || []).filter(f => f.module === module);

            return {

                id: module,

                module,

                risk: riskMap[module] || 0,

                normalizedRisk: (riskMap[module] || 0) / maxRisk,

                findings: [
                    ...moduleFindings,
                    ...classFindings
                ],

                classes: classFindings.length
            };

        });

        const links =
            (raw.moduleDependencies || []).map(l => ({
                source: l.from,
                target: l.to,
                weight: l.weight
            }));

        const degree = {};

        links.forEach(l => {

            degree[l.source] = (degree[l.source] || 0) + 1;

            degree[l.target] = (degree[l.target] || 0) + 1;

        });

        nodes.forEach(n => {

            n.degree = degree[n.id] || 0;

        });

        const degrees = nodes.map(n => n.degree);

        const sortedDegrees = [...degrees].sort((a, b) => a - b);

        const highDegreeThreshold = sortedDegrees[Math.floor(sortedDegrees.length * 0.8)] || 0;

        const lowDegreeThreshold = sortedDegrees[Math.floor(sortedDegrees.length * 0.2)] || 0;

        nodes.forEach(n => {

            n.dependencies = links
                .filter(
                    l => l.source === n.id
                )
                .map(l => l.target);

            n.usedBy = links
                .filter(l => l.target === n.id)
                .map(l => l.source);

        });

        nodes.forEach(n => {

            if (n.degree >= highDegreeThreshold) {

                n.role = "Integration hub";

            } else if (n.degree <= lowDegreeThreshold) {

                n.role = "Leaf module";

            } else if (
                n.degree > 0
            ) {

                n.role = "Business module";

            } else {

                n.role = "Isolated module";

            }

            n.reasoning = [`Connected with ${n.degree} modules`, `Depends on ${ n.dependencies?.length || 0
                } modules`, `Used by ${ n.usedBy?.length || 0} modules`, `Contains ${ n.classes || 0 } classes`

            ];

        });

        return {
            nodes,
            links
        };

    }, [graph, viewMode]);

    const searchResults = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        if (!query || viewMode !== "classes") {
            return [];
        }

        return data.nodes
            .filter(node =>
                node.id.toLowerCase().includes(query) ||
                node.className?.toLowerCase().includes(query)
            )
            .slice(0, 10);

    }, [data.nodes, searchQuery, viewMode]);

    useEffect(() => { function updateSize() {

            if (!graphRef.current) {
                return;
            }

            setGraphSize({
                width: graphRef.current.clientWidth,

                height: graphRef.current.clientHeight
            });

        }

        updateSize();

        window.addEventListener("resize", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
        };

    }, []);

    useEffect(() => {
        const fg = forceGraphRef.current;

        if (!fg || viewMode !== "classes" || !data.nodes.length) {
            return;
        }

        const maxDegree = Math.max(1, ...data.nodes.map(node => node.degree || 0));

        let simulationNodes = [];

        const radialForce = alpha => {
            simulationNodes.forEach(node => {
                if (typeof node.x !== "number" || typeof node.y !== "number") {
                    return;
                }

                const x = node.x;
                const y = node.y;
                const radius = Math.sqrt(x * x + y * y) || 1;

                const targetRadius =
                    90 + (1 - (node.degree || 0) / maxDegree) * 260;

                const force = (targetRadius - radius) * 0.02 * alpha;

                node.vx += (x / radius) * force;
                node.vy += (y / radius) * force;
            });
        };

        radialForce.initialize = nodes => {
            simulationNodes = nodes;
        };

        fg.d3Force("radial", radialForce);
        fg.d3ReheatSimulation();

        return () => {
            fg.d3Force("radial", null);
        };

    }, [data, viewMode]);

    const activeNode = selectedNode || hoverNode;

    const relatedNodeIds = new Set([...(activeNode?.dependencies || []), ...(activeNode?.usedBy || [])]);

    if (!data.nodes.length) {

        return (<div>Architecture graph is empty</div>);
    }


    const graphComplexity = data.nodes.length;

    const cooldown = graphComplexity > 150 ? 50 : 100;

    const alphaDecay = graphComplexity > 150 ? 0.05 : 0.02;

    return (

        <div style={{ height: "700px" }}>

            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <button onClick={() => setViewMode("modules")}>
                    Modules
                </button>

                <button onClick={() => setViewMode("classes")}>
                    Classes
                </button>
            </div>

            {viewMode === "classes" && (
                <div style={{ position: "relative", marginBottom: "12px", maxWidth: "450px" }}>
                    <input
                        type="text"
                        placeholder="Search class by name..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{
                            width: "100%",
                            padding: "10px 12px",
                            boxSizing: "border-box",
                            background: "#1a1a1a",
                            color: "#fff",
                            border: "1px solid #444",
                            borderRadius: "6px",
                            fontSize: "14px"
                        }}
                    />

                    {searchResults.length > 0 && (
                        <div style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#222",
                            border: "1px solid #444",
                            borderRadius: "6px",
                            zIndex: 20,
                            maxHeight: "300px",
                            overflowY: "auto"
                        }}>
                            {searchResults.map(node => (
                                <div
                                    key={node.id}
                                    onClick={() => {
                                        setSelectedNode(node);
                                        setHoverNode(null);
                                        setSearchQuery(node.className || node.id);

                                        if (
                                            typeof node.x === "number" &&
                                            typeof node.y === "number"
                                        ) {
                                            forceGraphRef.current?.centerAt(
                                                node.x,
                                                node.y,
                                                600
                                            );

                                            forceGraphRef.current?.zoom(2, 600);
                                        }
                                    }}
                                    style={{
                                        padding: "10px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #333"
                                    }}
                                >
                                    <div style={{ color: "#fff", fontSize: "14px" }}>
                                        {node.className || node.id}
                                    </div>

                                    <div style={{ color: "#888", fontSize: "12px" }}>
                                        {node.module} · {node.packageName}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Legend */}

            <div
                style={{ display: "flex", justifyContent: "flex-start", paddingLeft: "250px", gap: "20px",
                    alignItems: "center", marginBottom: "12px", color: "#ccc", fontSize: "14px", width: "100%"}}>

                <span>
                    <span style={{ color: "#34c759" }}> ● </span>
                    {" "}
                    Low risk
                </span>

                <span>
                    <span style={{ color: "#ff9500" }}> ● </span>
                    {" "}
                    Medium risk
                </span>

                <span>
                    <span style={{ color: "#ff3b30"}}> ● </span>
                    {" "} Critical hotspot
                </span>

            </div>


            <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 0, minWidth: 0 }}>

                {/* GRAPH */}

                <div ref={graphRef} style={{ width: "100%", height: "100%", minWidth: 0 }}>

                    <ForceGraph2D
                        ref={forceGraphRef}
                        graphData={data}

                        backgroundColor="#111"

                        width={
                            graphSize.width
                        }

                        height={
                            graphSize.height
                        }


                        /** Hover is temporary.*/
                        onNodeHover={node => {

                            setHoverNode(node || null);

                        }}


                        /** Click creates persistent
                         * architecture focus.
                         */
                        onNodeClick={node => {

                            setSelectedNode(node || null);

                        }}

                        /*
                         * Clicking empty canvas
                         * removes persistent focus.
                         */
                        onBackgroundClick={() => {
                            setSelectedNode(null);
                        }}

                        /*
                         * Links become darker when
                         * a module is focused.
                         */
                        linkColor={link => {

                            if (!selectedNode) {
                                return "#444";
                            }


                            const sourceId = typeof link.source === "object" ? link.source.id : link.source;

                            const targetId = typeof link.target === "object" ? link.target.id : link.target;

                            const isFocusLink = sourceId === selectedNode.id || targetId === selectedNode.id;

                            return isFocusLink ? "#888" : "#222"; }}

                        linkDirectionalArrowLength={4}

                        linkDirectionalArrowRelPos={1}

                        /** Focus links are thicker.*/

                        linkWidth={link => {

                            const baseWidth = Math.max(1, Math.min((link.weight || 0) / 120, 6));

                            if (!selectedNode) {
                                return baseWidth;
                            }

                            const sourceId = typeof link.source === "object" ? link.source.id : link.source;

                            const targetId = typeof link.target === "object" ? link.target.id : link.target;

                            const isFocusLink = sourceId === selectedNode.id || targetId === selectedNode.id;

                            return isFocusLink ? Math.max(2, baseWidth) : 1;
                        }}

                        cooldownTicks={
                            cooldown
                        }

                        d3AlphaDecay={
                            alphaDecay
                        }

                        d3VelocityDecay={0.15}

                        nodeRelSize={6}

                        /** Custom architecture nodes.*/
                        nodeCanvasObject={
                            (node, ctx) => {

                                if (typeof node.x !== "number" || typeof node.y !== "number") {
                                    return;
                                }

                                const isFocused = selectedNode?.id === node.id;

                                const isRelated = activeNode && relatedNodeIds.has(node.id);

                                const isDimmed = selectedNode && !isFocused && !isRelated;

                                const size = isFocused ? 11 : isDimmed ? 2 : 4 + Math.min((node.degree || 0) / 3, 10);

                                /** Node color.*/

                                if (isDimmed) {
                                    ctx.fillStyle = "#252525";
                                } else if (isFocused) {
                                    ctx.fillStyle = "#ffffff";
                                } else if (node === hoverNode) {
                                    ctx.fillStyle = "#ffffff";
                                } else if (node.normalizedRisk >= 0.75) {
                                    ctx.fillStyle = "#ff3b30";
                                } else if (node.normalizedRisk >= 0.35) {
                                    ctx.fillStyle = "#ff9500";
                                } else {
                                    ctx.fillStyle = "#34c759";
                                }

                                /** Draw node.*/
                                ctx.beginPath();
                                ctx.arc(node.x, node.y, size, 0, Math.PI * 2);
                                ctx.fill();

                                /** Show labels for:
                                 *
                                 * - focused node
                                 * - direct neighbors
                                 * - hovered node
                                 * - critical nodes
                                 * - integration hubs
                                 */

                                const showLabel = isFocused || node === hoverNode;

                                if (showLabel) {

                                    ctx.font = isFocused ? "bold 12px Arial" : "10px Arial";

                                    ctx.fillStyle = isDimmed ? "#555" : "#ffffff";

                                    ctx.textAlign = "left";
                                    ctx.fillText(isFocused ? (node.className || node.id.split("/").pop()) : node.className || node.id,
                                        node.x + size + 5, node.y + 3);
                                }
                            }}
                    />
                </div>

                {/* RIGHT DETAILS PANEL */}

                {(selectedNode || hoverNode) && (
                    <div style={{ position: "absolute", top: 0, right: 0, width: "320px", height: "100%", boxSizing: "border-box",
                            background: "#111", color: "#fff", padding: "20px", borderLeft: "1px solid #333", overflowY: "auto",
                            zIndex: 10, pointerEvents: selectedNode ? "auto" : "none" }}> {(() => {

                        const activeNode = selectedNode || hoverNode;

                        if (!activeNode) {

                            return ( <div style={{ color: "#777", fontSize: "14px", textAlign: "center", marginTop: "40px" }} >
                                Hover or click a module <br />to inspect architecture details
                            </div>);
                        }

                        return (
                            <>
                                <h3 style={{ marginTop: 0, marginBottom: "16px", color: "#ff4444" }}> {activeNode.id} </h3>

                                <div style={{ marginBottom: "12px"}}>

                                    <div style={{ color: "#888", fontSize: "13px" }}> Connections </div>

                                    <div style={{ fontSize: "18px" }}> {activeNode.degree } </div>

                                </div>

                                <div style={{ marginBottom: "12px" }}>

                                    <div style={{ color: "#888", fontSize: "13px" }}> Risk score </div>

                                    <div style={{ fontSize: "18px" }}> {activeNode.risk} </div>

                                </div>

                                <div style={{ marginBottom: "12px" }}>

                                    <div style={{ color: "#888", fontSize: "13px" }}> Module role </div>

                                    <div style={{ fontSize: "18px" }}> {activeNode.role} </div>

                                </div>

                                <div style={{ padding: "12px", borderRadius: "10px", background: activeNode.normalizedRisk >= 0.75

                                                ? "rgba(255,59,48,0.15)" : activeNode.normalizedRisk >= 0.35
                                                    ? "rgba(255,149,0,0.15)" : "rgba(52,199,89,0.15)", border: activeNode.normalizedRisk >= 0.75
                                            ? "1px solid #ff3b30" : activeNode.normalizedRisk >= 0.35 ? "1px solid #ff9500" : "1px solid #34c759" }}>

                                    <div style={{ marginBottom: "12px" }} >

                                        <div style={{ color: "#888", fontSize: "13px" }} > Depends on </div>

                                        <div style={{ fontSize: "14px", color: "#ddd" }}>
                                            { activeNode.dependencies?.length ? activeNode.dependencies.join(", ") : "None"}
                                        </div>

                                    </div>

                                    <div style={{ marginBottom: "12px" }} >
                                        <div style={{ color: "#888", fontSize: "13px" }}> Used by </div>

                                        <div style={{ fontSize: "14px", color: "#ddd" }}>
                                            { activeNode.usedBy?.length ? activeNode.usedBy.join(", ") : "None" }
                                        </div>
                                    </div>

                                    <div style={{ fontWeight: 600, marginBottom: "6px" }}> Architectural assessment </div>

                                    <div style={{ color: "#ddd", lineHeight: 1.5 }}>
                                        { activeNode.normalizedRisk >= 0.75 ? "Critical coupling hotspot. Changes in this module are likely to impact multiple parts of the system."

                                                : activeNode.normalizedRisk >= 0.35 ? "Elevated architectural risk caused by high dependency concentration."

                                                    : "Relatively stable module with no significant architectural hotspot detected."}
                                    </div>
                                </div>

                                <div style={{ marginTop: "16px", padding: "12px", borderRadius: "10px", background: "#1a1a1a", border: "1px solid #333" }} >

                                    <div style={{ fontWeight: 600, marginBottom: "8px" }} > Reasoning</div>

                                    <div style={{ color: "#ccc", lineHeight: 1.5, fontSize: "14px" }}>

                                        {activeNode.role === "Integration hub"
                                                ? "High architectural centrality. This module connects multiple parts of the system and may have a wide impact radius."
                                                : activeNode.role === "Leaf module"
                                                    ? "Low dependency influence. Changes in this module are unlikely to affect large parts of the system."
                                                    : ( <ul> {activeNode.reasoning?.map((r, i) =>
                                                        <li key={i}>{r}</li>)}
                                                        </ul>)}
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>
                )}
            </div>
        </div>
    );
}